// @ts-nocheck
import { View, StyleSheet } from 'react-native';
import Text from '../../../components/Text';
import React from 'react';
import { Card } from 'react-native-paper';
import { Package } from '../types';
import ZoomableImage from '../../../components/ZoomableImage';
import Ionicons from '@expo/vector-icons/Ionicons';
import Feather from '@expo/vector-icons/Feather';

function statusIcon(status?: string): {name: string; color: string} {
  switch (status) {
    case 'Claimed':
      return {name: 'checkmark-circle', color: '#16a34a'};
    case 'Unclaimed':
      return {name: 'time-outline', color: '#d97706'};
    default:
      return {name: 'cube-outline', color: '#6366f1'};
  }
}

function StatusChip({status}: {status?: string}) {
  const {name, color} = statusIcon(status);
  const isClaimed = status === 'Claimed';
  const isUnclaimed = status === 'Unclaimed';
  const bg = isClaimed ? '#dcfce7' : isUnclaimed ? '#fef3c7' : '#eef2ff';

  return (
    <View style={[chipStyles.chip, {backgroundColor: bg}]}>
      <Ionicons name={name} size={11} color={color} />
      <Text style={[chipStyles.label, {color}]}>{status || 'PACKAGE'}</Text>
    </View>
  );
}

const chipStyles = StyleSheet.create({
  chip: {
    flexDirection: 'row',
    alignItems: 'center',
    borderRadius: 5,
    paddingHorizontal: 7,
    paddingVertical: 2,
    gap: 4,
    alignSelf: 'flex-start',
  },
  label: {
    fontSize: 10,
    fontWeight: '700',
    textTransform: 'capitalize',
  },
});

export default function PackagesItem({
  packageItem,
  itemClickHandler,
}: {
  packageItem: Package;
  itemClickHandler: () => void;
}) {
  const imageUrl = packageItem.receivedImage
    ? `data:image/jpeg;base64,${packageItem.receivedImage}`
    : null;

  const isUnclaimed = packageItem.status === 'Unclaimed';

  return (
    <Card
      style={[localStyles.card, isUnclaimed && localStyles.unclaimedCard]}
      onPress={() => itemClickHandler()}>
      <View style={localStyles.container}>
        {/* Left Column: Image (rendered only when photo exists) */}
        {!!imageUrl && (
          <View style={localStyles.imageColumn}>
            <ZoomableImage
              title="Package Received"
              style={localStyles.thumbnailImage}
              source={{ uri: imageUrl }}
            />
          </View>
        )}

        {/* Content Column */}
        <View style={localStyles.contentColumn}>
          {/* Header row: Badge + Status + Date */}
          <View style={localStyles.headerRow}>
            <View style={localStyles.badgeRow}>
              {!!packageItem.status && <StatusChip status={packageItem.status} />}
            </View>
            <View style={localStyles.dateChevron}>
              <Text style={localStyles.dateText}>
                {packageItem.dateTimeRecieved
                  ? new Date(packageItem.dateTimeRecieved).toLocaleDateString()
                  : ''}
              </Text>
              <Feather name="chevron-right" size={18} color="#a1a1aa" />
            </View>
          </View>

          {/* Tracking Number Title */}
          <Text numberOfLines={1} style={localStyles.trackingNumber}>
            {packageItem.trackingNumber
              ? `Tracking #${packageItem.trackingNumber}`
              : 'No tracking number'}
          </Text>

          {/* Metadata Rows */}
          <View style={localStyles.metaContainer}>
            {!!packageItem.from && (
              <View style={localStyles.metaRow}>
                <Text numberOfLines={1} style={localStyles.metaText}>
                  {`From: ${packageItem.from}`}
                </Text>
              </View>
            )}
            {(!!packageItem.unit?.unit || !!packageItem.location) && (
              <View style={localStyles.metaRow}>
                <Feather name="map-pin" size={12} color="#71717a" />
                <Text numberOfLines={1} style={localStyles.metaText}>
                  {[
                    packageItem.unit?.unit ? `Unit ${packageItem.unit.unit}` : null,
                    packageItem.location ? packageItem.location : null,
                  ]
                    .filter(Boolean)
                    .join(' · ')}
                </Text>
              </View>
            )}
            {!!packageItem.whoClaimedPackage && (
              <View style={localStyles.metaRow}>
                <Feather name="check-circle" size={12} color="#16a34a" />
                <Text numberOfLines={1} style={localStyles.claimedByText}>
                  {`Claimed by ${packageItem.whoClaimedPackage}${
                    packageItem.claimedDateTime
                      ? ` on ${new Date(packageItem.claimedDateTime).toLocaleDateString()}`
                      : ''
                  }`}
                </Text>
              </View>
            )}
          </View>
        </View>
      </View>
    </Card>
  );
}

const localStyles = StyleSheet.create({
  card: {
    borderRadius: 10,
    borderColor: '#e4e4e7',
    borderWidth: 1,
    elevation: 1,
    marginHorizontal: 12,
    marginVertical: 5,
    overflow: 'hidden',
  },
  unclaimedCard: {
    borderLeftColor: '#d97706',
    borderLeftWidth: 4,
  },
  container: {
    flexDirection: 'row',
    alignItems: 'flex-start',
    paddingHorizontal: 12,
    paddingVertical: 10,
    gap: 10,
  },
  imageColumn: {
    alignSelf: 'flex-start',
    marginTop: 2,
  },
  thumbnailImage: {
    width: 44,
    height: 44,
    borderRadius: 8,
  },
  imagePlaceholder: {
    width: 44,
    height: 44,
    borderRadius: 8,
    backgroundColor: '#eef2ff',
    alignItems: 'center',
    justifyContent: 'center',
  },
  contentColumn: {
    flex: 1,
    minWidth: 0,
    gap: 3,
  },
  headerRow: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    gap: 8,
  },
  badgeRow: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 6,
    flexWrap: 'wrap',
    flex: 1,
  },
  typeBadge: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 4,
    backgroundColor: '#eef2ff',
    borderRadius: 999,
    paddingHorizontal: 8,
    paddingVertical: 2,
    alignSelf: 'flex-start',
  },
  typeBadgeText: {
    color: '#6366f1',
    fontSize: 10,
    fontWeight: '700',
    textTransform: 'uppercase',
  },
  dateChevron: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 4,
    flexShrink: 0,
  },
  dateText: {
    color: '#71717a',
    fontSize: 11,
  },
  trackingNumber: {
    color: '#18181b',
    fontSize: 14,
    fontWeight: '700',
    marginTop: 2,
  },
  metaContainer: {
    gap: 3,
    marginTop: 2,
  },
  metaRow: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 6,
  },
  metaText: {
    color: '#52525b',
    fontSize: 12,
    flexShrink: 1,
  },
  claimedByText: {
    color: '#16a34a',
    fontSize: 12,
    fontWeight: '600',
    flexShrink: 1,
  },
});
